// src/pages/Storage.tsx — matches iOS StorageManagementView
// Storage is managed via the subscription plan (Change Plan) or a Custom Plan
// request. There are NO à-la-carte storage unit packs (parity with the apps).
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, HardDrive, Star, Building2, User as UserIcon, Sparkles, Check, Rocket, LogOut } from "lucide-react";
import { apiService } from "../services/api";
import { useAuth } from "../context/AuthContext";

const GB = 1_073_741_824;
const fmtGb = (n: number) => (n >= 100 ? n.toFixed(0) : n.toFixed(1));

export default function Storage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [details, setDetails] = useState<any>(null);
  const [sub, setSub] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Theme State
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.classList.contains("dark")
  );

  // Custom plan request
  const [showCustom, setShowCustom] = useState(false);
  const [requestedGb, setRequestedGb] = useState<string>("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleTheme = () => {
    if (isDarkMode) {
      document.documentElement.classList.remove("dark");
      setIsDarkMode(false);
    } else {
      document.documentElement.classList.add("dark");
      setIsDarkMode(true);
    }
  };

  const handleLogout = async () => {
    try {
      if (typeof apiService?.logout === "function") {
        apiService.logout().catch(() => {});
      }
      localStorage.clear();
      sessionStorage.clear();
      navigate("/login", { replace: true });
    } catch (e) {}
  };

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        apiService.getStorageDetails(),
        apiService.getSubscription().catch(() => null),
      ]);
      setDetails(d);
      setSub(s);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const usedBytes = details?.usedBytes ?? details?.used_bytes ?? details?.summary?.usedBytes ?? 0;
  const totalGb = details?.quotaGb ?? details?.quota_gb ?? details?.totalGb ?? details?.summary?.baseStorageGb ?? 0;
  const isUnlimited = details?.isUnlimited ?? details?.is_unlimited ?? false;
  const usedGb = usedBytes / GB;
  const pct = isUnlimited || !totalGb ? 0 : Math.min(100, (usedGb / totalGb) * 100);
  const mediaCount = details?.summary?.mediaCount ?? details?.mediaCount;

  const planName = (sub?.planName || sub?.plan?.name || user?.tier || "FREE").toUpperCase();
  const planDisplay = planName === "PRO" ? "Pro" : planName === "ENTERPRISE" ? "Enterprise" : planName === "FREE" ? "Free" : planName;
  const PlanIcon = planName === "PRO" ? Star : planName === "ENTERPRISE" ? Building2 : UserIcon;
  const planColor = planName === "PRO" ? "text-blue-400" : planName === "ENTERPRISE" ? "text-purple-400" : "text-gray-400";
  const planBadgeBg = planName === "PRO" ? "bg-blue-500/20 text-blue-300" : planName === "ENTERPRISE" ? "bg-purple-500/20 text-purple-300" : "bg-gray-500/20 text-gray-300";
  const storageText = isUnlimited ? "Unlimited data capacity" : `${fmtGb(totalGb)}GB data capacity included`;

  const submitCustom = async () => {
    setSubmitting(true);
    try {
      const gb = requestedGb ? parseInt(requestedGb, 10) : undefined;
      await apiService.requestCustomPlan({
        requestedStorageGb: gb,
        message: message || undefined,
        contactEmail: user?.email,
      });
      setSubmitted(true);
      setShowCustom(false);
    } catch (e: any) {
      alert(e?.response?.data?.error || e?.message || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-[#0a0a0a] text-black dark:text-white transition-colors duration-300 font-['Product_Sans']">
      
      {/* Navbar */}
      <nav className="
        flex items-center justify-between
        h-[72px] px-6 md:px-10
        bg-white dark:bg-black
        border-b border-gray-200 dark:border-[#2C2C2C]
        transition-colors duration-300
        sticky top-0 z-50
      ">
        <div className="flex-1 flex justify-start">
          <span 
            onClick={() => navigate("/dashboard")}
            className="font-['Caviar_Dreams'] text-[24px] md:text-[28px] text-black dark:text-white tracking-widest cursor-pointer"
          >
            VASTAVIK
          </span>
        </div>

        <div className="hidden md:flex flex-1 justify-center items-center gap-8 md:gap-12 h-full">
          {(["dashboard", "library", "upload", "profile"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => navigate(tab === "dashboard" ? "/dashboard" : `/${tab}`)}
              className={`relative h-full flex flex-col items-center justify-center font-['Product_Sans'] text-[15px] transition-colors ${
                tab === "profile" 
                  ? "text-black dark:text-white font-bold"
                  : "text-gray-500 dark:text-[#A0A0A0] hover:text-black dark:hover:text-white font-normal"
              }`}
            >
              <span className="capitalize pb-1">{tab}</span>
              {tab === "profile" && (
                <span className="absolute bottom-0 left-0 w-full h-[2px] bg-black dark:bg-white rounded-t-sm" />
              )}
            </button>
          ))}
        </div>

        <div className="flex-1 flex justify-end items-center gap-4">
          <button
            onClick={toggleTheme}
            title="Toggle Theme"
            className="text-gray-400 hover:text-black dark:hover:text-white transition-colors flex items-center justify-center"
          >
            {isDarkMode ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
            )}
          </button>
          <div className="hidden sm:block text-[13px] text-gray-700 dark:text-[#E0E0E0] font-['Product_Sans'] truncate max-w-[150px] lg:max-w-[200px]">
            {user?.email ?? ""}
          </div>
          <button
            onClick={handleLogout}
            title="Log out"
            className="text-[#E57373] hover:opacity-80 transition-opacity flex items-center justify-center ml-2"
          >
            <LogOut size={20} strokeWidth={2} />
          </button>
        </div>
      </nav>

      <div className="max-w-[1104px] mx-auto p-6 md:p-10 pb-24">
        {/* Header */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-black dark:hover:text-white transition mb-8"
        >
          <ChevronLeft size={16} />
          <span>Back</span>
        </button>

        <h1 className="font-['Caviar_Dreams'] text-[40px] md:text-[44px] leading-tight font-bold text-black dark:text-white mb-3">
          Data Capacity
        </h1>
        <p className="text-[15px] text-gray-500 dark:text-gray-400 font-light mb-12">
          Manage the data capacity included with your plan and monitor your usage.
        </p>

        {loading ? (
          <div className="grid gap-[32px] lg:grid-cols-12">
            <div className="lg:col-span-5 h-[550px] rounded-[12px] bg-gray-200 dark:bg-[#121212] border border-gray-300 dark:border-[#2C2C2C] animate-pulse" />
            <div className="lg:col-span-7 flex flex-col gap-[32px]">
              <div className="h-[313px] rounded-[12px] bg-gray-200 dark:bg-[#121212] border border-gray-300 dark:border-[#2C2C2C] animate-pulse" />
              <div className="h-[212px] rounded-[12px] bg-gray-200 dark:bg-[#121212] border border-gray-300 dark:border-[#2C2C2C] animate-pulse" />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-[32px]">
            {/* Left Column: Plan Details */}
            <div className="lg:col-span-5 bg-white dark:bg-[#121212] rounded-[12px] p-[40px] border border-gray-200 dark:border-[#2C2C2C] h-auto lg:h-[550px] flex flex-col shadow-sm dark:shadow-none">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-[28px] font-semibold text-black dark:text-white">
                  {planDisplay} Plan
                </h2>
                <span className="px-3.5 py-1.5 text-[10px] font-bold tracking-wider text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-[#333333] rounded-full uppercase">
                  Current
                </span>
              </div>
              <p className="text-[15px] text-gray-500 dark:text-gray-400 font-light mb-10">
                Essential features to get you started.
              </p>

              <div className="text-[11px] font-semibold text-gray-500 tracking-wider uppercase mb-6">
                Plan Details
              </div>

              <div className="space-y-4 mb-auto">
                <div className="flex items-center gap-3">
                  <div className="bg-black dark:bg-white rounded-full flex items-center justify-center w-[18px] h-[18px] shrink-0">
                    <Check size={12} className="text-white dark:text-black stroke-[4]" />
                  </div>
                  <span className="text-[15px] text-gray-700 dark:text-gray-300 font-light">
                    {isUnlimited ? "Unlimited data capacity" : `${fmtGb(totalGb)}GB secure data storage`}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-black dark:bg-white rounded-full flex items-center justify-center w-[18px] h-[18px] shrink-0">
                    <Check size={12} className="text-white dark:text-black stroke-[4]" />
                  </div>
                  <span className="text-[15px] text-gray-700 dark:text-gray-300 font-light">
                    Standard media authentication
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="bg-black dark:bg-white rounded-full flex items-center justify-center w-[18px] h-[18px] shrink-0">
                    <Check size={12} className="text-white dark:text-black stroke-[4]" />
                  </div>
                  <span className="text-[15px] text-gray-700 dark:text-gray-300 font-light">
                    Basic library management
                  </span>
                </div>
              </div>

              <button
                onClick={() => navigate("/pricing")}
                className="mt-12 w-full py-3.5 rounded-[8px] bg-gray-100 dark:bg-[#1F1F1F] text-black dark:text-white font-medium hover:bg-gray-200 dark:hover:bg-[#2a2a2a] transition"
              >
                Change Plan
              </button>
            </div>

            {/* Right Column: Usage & Enterprise */}
            <div className="lg:col-span-7 flex flex-col gap-[32px]">
              {/* Usage Card */}
              <div className="bg-white dark:bg-[#121212] rounded-[12px] p-[40px] border border-gray-200 dark:border-[#2C2C2C] h-auto lg:h-[313px] flex flex-col shadow-sm dark:shadow-none">
                <h2 className="text-[22px] font-semibold text-black dark:text-white mb-1">
                  Data Capacity
                </h2>
                <p className="text-[15px] text-gray-500 dark:text-gray-400 font-light mb-8">
                  Your current storage consumption
                </p>

                <div className="mt-auto border border-gray-200 dark:border-[#4B4B4B] rounded-[8px] p-[24px] h-[125px] flex flex-col justify-between">
                  <div className="flex justify-between items-center">
                    <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 tracking-wider uppercase">
                      Available
                    </span>
                    <span className="text-[12px] text-gray-500">
                      {isUnlimited ? "Unlimited" : `${Math.round(pct)}% used`}
                    </span>
                  </div>
                  
                  <div className="text-[32px] md:text-[36px] leading-none font-semibold text-black dark:text-white mt-1 mb-2">
                    {isUnlimited ? "Unlimited" : `${fmtGb(totalGb)}GB`}
                  </div>

                  {!isUnlimited && (
                    <div className="w-full h-1 bg-gray-200 dark:bg-[#333333] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-black dark:bg-white rounded-full transition-all"
                        style={{ width: `${Math.min(100, pct)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Enterprise Needs */}
              <div className="bg-white dark:bg-[#121212] rounded-[12px] p-[40px] border border-gray-200 dark:border-[#2C2C2C] h-auto lg:h-[212px] flex flex-col md:flex-row items-start md:items-center justify-between gap-6 shadow-sm dark:shadow-none">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Rocket size={20} className="text-black dark:text-white" />
                    <h3 className="text-[22px] font-semibold text-black dark:text-white">
                      Enterprise Needs?
                    </h3>
                  </div>
                  <p className="text-[15px] text-gray-500 dark:text-gray-400 font-light leading-relaxed md:max-w-[95%]">
                    Require larger capacity or specialized features? Request a custom plan tailored specifically to your organization's workflow.
                  </p>
                </div>

                <div className="w-full md:w-auto shrink-0 flex items-center justify-end">
                  {submitted ? (
                    <div className="text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-400/10 px-5 py-3.5 rounded-[8px] text-center">
                      ✓ Request submitted.
                    </div>
                  ) : showCustom ? (
                    <div className="flex flex-col gap-3 w-full md:min-w-[260px]">
                      <input
                        type="number"
                        min={1}
                        value={requestedGb}
                        onChange={(e) => setRequestedGb(e.target.value)}
                        placeholder="Capacity (GB)"
                        className="w-full rounded-[8px] border border-gray-300 dark:border-[#4B4B4B] bg-transparent dark:bg-black px-4 py-2.5 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      />
                      <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        rows={2}
                        placeholder="Message (optional)"
                        className="w-full rounded-[8px] border border-gray-300 dark:border-[#4B4B4B] bg-transparent dark:bg-black px-4 py-2.5 text-sm text-black dark:text-white focus:outline-none focus:border-black dark:focus:border-white resize-none"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowCustom(false)}
                          className="flex-1 py-2.5 rounded-[8px] border border-gray-300 dark:border-[#4B4B4B] text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={submitCustom}
                          disabled={submitting}
                          className="flex-1 py-2.5 rounded-[8px] bg-black dark:bg-white text-white dark:text-black font-medium hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 transition text-sm"
                        >
                          {submitting ? "..." : "Submit"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowCustom(true)}
                      className="w-full md:w-auto px-6 py-3.5 rounded-[8px] bg-black dark:bg-white text-white dark:text-black font-medium hover:bg-gray-800 dark:hover:bg-gray-200 transition whitespace-nowrap shadow-sm"
                    >
                      Request a custom plan
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}